/**
 * WindowNarrative — three-block narrative layout shared across every window.
 *
 * Layout:
 *   ┌──────────────────┐ ┌──────────────────┐
 *   │ Introduction     │ │ Observations     │
 *   │ (sky-blue, "i")  │ │ (amber, "!")     │
 *   └──────────────────┘ └──────────────────┘
 *   ┌────────────────────────────────────────┐
 *   │ Key Findings — applicable to next draw │
 *   │ (emerald, "★")                         │
 *   └────────────────────────────────────────┘
 *
 * Each block accepts `title`, optional `body`, and a `bullets` array.
 * Bullets can be plain strings, React elements, or { content, hint } objects.
 *
 * Each block also exposes a "⚠ Report inaccurate description" link that opens
 * a dialog letting the user submit a correction. Reports are POSTed to
 * /api/narrative-reports and are reviewable by admin via /api/admin/narrative-reports.
 */
import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const renderBullet = (b, key) => {
  if (b == null) return null;
  if (typeof b === 'string' || typeof b === 'number') return <li key={key}>{b}</li>;
  if (b.$$typeof) return <li key={key}>{b}</li>; // React element
  // Object form { content, hint }
  if (typeof b === 'object') {
    return (
      <li key={key}>
        {b.content}
        {b.hint && <div className="text-xs text-gray-600 mt-1 ml-6 italic">{b.hint}</div>}
      </li>
    );
  }
  return null;
};

/**
 * Inline link + dialog for reporting inaccurate narrative content.
 * `block` identifies which block was reported (introduction / observations / key-findings / general).
 */
export function ReportInaccuracyLink({ block = 'introduction', windowId, accentColor = 'text-sky-700 hover:text-sky-900' }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (message.trim().length < 5) {
      toast.error('Please describe the issue (5+ characters).');
      return;
    }
    setSubmitting(true);
    try {
      const pagePath = typeof window !== 'undefined' ? window.location.pathname : '';
      await axios.post(`${API}/api/narrative-reports`, {
        page_path: pagePath,
        window_id: windowId || null,
        block,
        message: message.trim(),
      }, { withCredentials: true });
      toast.success('Report submitted — thank you for the feedback!');
      setMessage('');
      setOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Submission failed';
      toast.error(`Could not submit: ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`mt-3 text-[11px] font-semibold ${accentColor} underline-offset-2 hover:underline transition`}
        data-testid={`report-inaccuracy-${block}`}
      >
        ⚠ Report inaccurate description
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Report inaccurate description</DialogTitle>
            <DialogDescription>
              Describe what is incorrect in this {block.replace('-', ' ')} block. Include the algorithm name or expected behaviour. Reports are reviewed by the admin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g. This window is based on A-X (consecutive draw, same position) — not B2-B1 within-draw gaps..."
              rows={6}
              className="text-sm"
              data-testid="report-inaccuracy-textarea"
            />
            <p className="text-[11px] text-gray-500">
              Page: <code className="font-mono bg-gray-100 px-1 py-0.5 rounded">{typeof window !== 'undefined' ? window.location.pathname : ''}</code>
              {windowId && <> · Window: <code className="font-mono bg-gray-100 px-1 py-0.5 rounded">{windowId}</code></>}
              {' · Block: '}<code className="font-mono bg-gray-100 px-1 py-0.5 rounded">{block}</code>
            </p>
          </div>
          <div className="flex gap-2 justify-end mt-3">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)} data-testid="report-cancel">Cancel</Button>
            <Button size="sm" onClick={handleSubmit} disabled={submitting} data-testid="report-submit">
              {submitting ? 'Submitting…' : 'Submit report'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function NarrativeIntroBlock({ title = 'Introduction — What this window analyses', body, bullets = [], testid = 'narrative-introduction', windowId }) {
  return (
    <div className="bg-gradient-to-r from-sky-50 via-blue-50 to-indigo-50 border-2 border-sky-300 rounded-xl p-5 h-full" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="shrink-0 inline-flex w-10 h-10 rounded-full bg-sky-600 text-white items-center justify-center font-black text-base">i</div>
        <div className="flex-1">
          <h2 className="text-base font-black text-sky-900 uppercase tracking-wider">{title}</h2>
          {body && <p className="text-sm text-sky-800 mt-1.5 mb-3">{body}</p>}
          {bullets.length > 0 && (
            <ol className="list-decimal list-inside space-y-1.5 text-sm text-gray-800 marker:font-black marker:text-sky-700">
              {bullets.map((b, i) => renderBullet(b, i))}
            </ol>
          )}
          <ReportInaccuracyLink block="introduction" windowId={windowId} accentColor="text-sky-700 hover:text-sky-900" />
        </div>
      </div>
    </div>
  );
}

export function NarrativeObservationsBlock({ title = 'Observations — Key findings', body, bullets = [], footer, testid = 'narrative-observations', windowId }) {
  return (
    <div className="bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 border-2 border-amber-400 rounded-xl p-5 h-full" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="shrink-0 inline-flex w-10 h-10 rounded-full bg-amber-600 text-white items-center justify-center font-black text-base">!</div>
        <div className="flex-1">
          <h2 className="text-base font-black text-amber-900 uppercase tracking-wider">{title}</h2>
          {body && <p className="text-sm text-amber-800 mt-1.5 mb-3">{body}</p>}
          {bullets.length > 0 && (
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-900 marker:font-black marker:text-amber-700">
              {bullets.map((b, i) => renderBullet(b, i))}
            </ol>
          )}
          {footer && <p className="text-xs text-amber-800 mt-3 italic">{footer}</p>}
          <ReportInaccuracyLink block="observations" windowId={windowId} accentColor="text-amber-700 hover:text-amber-900" />
        </div>
      </div>
    </div>
  );
}

export function NarrativeKeyFindingsBlock({ title, body, bullets = [], footer, badge, testid = 'narrative-key-findings', windowId }) {
  return (
    <div className="bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 border-2 border-emerald-500 rounded-xl p-5" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="shrink-0 inline-flex w-10 h-10 rounded-full bg-emerald-600 text-white items-center justify-center font-black text-base">★</div>
        <div className="flex-1">
          <h2 className="text-base font-black text-emerald-900 uppercase tracking-wider">{title || 'Key Findings — applicable to next draw'}</h2>
          {body && <p className="text-sm text-emerald-800 mt-1.5 mb-3">{body}</p>}
          {badge && (
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${badge.color || 'from-slate-500 to-slate-600'} text-white font-bold text-sm mb-4 shadow-md`} data-testid={`${testid}-badge`}>
              {badge.icon && <span className="text-base">{badge.icon}</span>}
              <span>{badge.label}</span>
              {badge.detail && <span className="text-xs opacity-90 font-mono">({badge.detail})</span>}
            </div>
          )}
          {bullets.length > 0 && (
            <ol className="list-decimal list-inside space-y-2.5 text-sm text-gray-900 marker:font-black marker:text-emerald-700">
              {bullets.map((b, i) => renderBullet(b, i))}
            </ol>
          )}
          {footer && <p className="text-xs text-emerald-700 mt-3 italic">{footer}</p>}
          <ReportInaccuracyLink block="key-findings" windowId={windowId} accentColor="text-emerald-700 hover:text-emerald-900" />
        </div>
      </div>
    </div>
  );
}

/**
 * Convenience wrapper: renders all three blocks in the standard layout.
 * Pass `intro`, `observations`, `keyFindings` props as objects.
 * If you need separate placement, use the individual block components.
 *
 * `windowId` (string like "W3", "W11") is auto-passed to every report dialog.
 */
export default function WindowNarrative({ intro, observations, keyFindings, windowId, className = '' }) {
  return (
    <div className={`space-y-5 ${className}`}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
        {intro && <NarrativeIntroBlock {...intro} windowId={windowId} />}
        {observations && <NarrativeObservationsBlock {...observations} windowId={windowId} />}
      </div>
      {keyFindings && <NarrativeKeyFindingsBlock {...keyFindings} windowId={windowId} />}
    </div>
  );
}
